import React from "react";
import { tv } from "tailwind-variants";

// Duplicated string constants
const BUTTON_ACCENT_SIFTED = "sifted" as const;
const BUTTON_ACCENT_PRO = "pro" as const;
const BUTTON_ACCENT_NEUTRAL = "neutral" as const;
const BUTTON_ACCENT_SUMMIT = "summit" as const;
const BUTTON_ACCENT_SUMMIT_2 = "summit2" as const;
const BUTTON_VARIANT_PRIMARY = "primary" as const;
const BUTTON_VARIANT_SECONDARY = "secondary" as const;
const BUTTON_SIZE_SM = "sm" as const;
const BUTTON_SIZE_MD = "md" as const;
const BUTTON_SIZE_LG = "lg" as const;

export const BUTTON_ACCENTS = {
  pro: BUTTON_ACCENT_PRO,
  sifted: BUTTON_ACCENT_SIFTED,
  neutral: BUTTON_ACCENT_NEUTRAL,
  summit: BUTTON_ACCENT_SUMMIT,
  summit2: BUTTON_ACCENT_SUMMIT_2,
} as const;
export type ButtonAccent = (typeof BUTTON_ACCENTS)[keyof typeof BUTTON_ACCENTS];

export const BUTTON_VARIANTS = {
  primary: BUTTON_VARIANT_PRIMARY,
  secondary: BUTTON_VARIANT_SECONDARY,
} as const;
export type ButtonVisualVariant =
  (typeof BUTTON_VARIANTS)[keyof typeof BUTTON_VARIANTS];

// NOTE: Refactored to align with Figma Design System v1.0 Text Button specs (node 15266-895)
// Implements all interaction states: default, hover, active, focused, disabled
// Exported so it can be shared with LinkButton
export const buttonStyles = tv({
  base: [
    // Layout & shape
    "relative inline-flex items-center justify-center rounded-full text-center font-bold",
    // Typography (Aktiv Grotesk via CSS variable)
    "font-neutral leading-[1.3]",
    // Focus styles - using shadow for inner border effect as per Figma
    "focus:outline-none focus-visible:outline-none",
    // Sizing defaults (md)
    "min-w-24 h-9 px-5 py-2",
    // Transition for subtle state feedback
    "transition-all duration-150",
    // Disabled handling (applied regardless of variant colors)
    "disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" "),
  variants: {
    variant: {
      // Solid button (no border by default, fills with accent)
      [BUTTON_VARIANT_PRIMARY]: "text-mono-00",
      // Outlined button (white / transparent surface with accent border & text)
      // NOTE: inset box-shadow instead of border-2 so the stroke does not add
      // to the element's auto/hug width (mirrors Figma inside-stroke behaviour).
      [BUTTON_VARIANT_SECONDARY]: "bg-mono-00",
    },
    accent: {
      [BUTTON_ACCENT_SIFTED]: "",
      [BUTTON_ACCENT_PRO]: "",
      [BUTTON_ACCENT_NEUTRAL]: "",
      [BUTTON_ACCENT_SUMMIT]: "",
      [BUTTON_ACCENT_SUMMIT_2]: "",
    },
    size: {
      [BUTTON_SIZE_SM]: "[font-size:16px] h-9 px-4 py-2 min-w-[96px]",
      [BUTTON_SIZE_MD]: "[font-size:16px] h-9 px-5 py-2 min-w-[96px]",
      [BUTTON_SIZE_LG]: "[font-size:16px] h-12 px-8 py-4 min-w-[112px]",
    },
    disabled: {
      true: "pointer-events-none",
    },
  },
  compoundVariants: [
    // ============================================================================
    // PRIMARY BUTTONS (Solid)
    // ============================================================================

    // Sifted Primary
    {
      variant: BUTTON_VARIANT_PRIMARY,
      accent: BUTTON_ACCENT_SIFTED,
      className: [
        "bg-sifted",
        "hover:bg-sifted-hover",
        "active:bg-sifted-active",
        // Focus: inner white border using inset shadow (Figma spec)
        "focus-visible:shadow-[inset_0_0_0_2px_rgb(255,255,255)]",
      ].join(" "),
    },

    // Pro Primary
    {
      variant: BUTTON_VARIANT_PRIMARY,
      accent: BUTTON_ACCENT_PRO,
      className: [
        "bg-pro",
        "hover:bg-pro-hover",
        "active:bg-pro-active",
        // Focus: inner white border using inset shadow (Figma spec)
        "focus-visible:shadow-[inset_0_0_0_2px_rgb(255,255,255)]",
      ].join(" "),
    },

    // Neutral Primary
    {
      variant: BUTTON_VARIANT_PRIMARY,
      accent: BUTTON_ACCENT_NEUTRAL,
      className: [
        "bg-brand-neutral",
        "hover:bg-brand-neutral-90",
        "active:bg-neutral-active",
        // Focus: inner white border using inset shadow (Figma spec)
        "focus-visible:shadow-[inset_0_0_0_2px_rgb(255,255,255)]",
      ].join(" "),
    },

    {
      variant: BUTTON_VARIANT_PRIMARY,
      accent: BUTTON_ACCENT_SUMMIT,
      className: [
        "bg-gradient-to-r from-summit-gradient-start to-summit-gradient-end text-black",
        "hover:from-summit-gradient-end hover:to-summit-gradient-start",
        "active:opacity-90",
        "focus-visible:shadow-[inset_0_0_0_2px_rgb(0,0,0)]",
      ].join(" "),
    },

    {
      variant: BUTTON_VARIANT_PRIMARY,
      accent: BUTTON_ACCENT_SUMMIT_2,
      className: [
        "bg-gradient-to-r from-summit-gradient-2-start to-summit-gradient-2-end text-black",
        "hover:from-summit-gradient-2-end hover:to-summit-gradient-2-start",
        "active:opacity-90",
        "focus-visible:shadow-[inset_0_0_0_2px_rgb(0,0,0)]",
      ].join(" "),
    },

    // ============================================================================
    // SECONDARY BUTTONS (Outlined)
    // ============================================================================

    // Sifted Secondary
    {
      variant: BUTTON_VARIANT_SECONDARY,
      accent: BUTTON_ACCENT_SIFTED,
      className: [
        "text-sifted",
        // Default inset border (inset shadow = no layout impact, matches Figma inside stroke)
        "shadow-[inset_0_0_0_2px_rgb(216,28,95)]",
        // Hover: 10% opacity background overlay (Figma: rgba(216,28,95,0.1))
        "hover:bg-sifted-10",
        // Active: 20% opacity background overlay (Figma: rgba(216,28,95,0.2))
        "active:bg-sifted-20",
        // Focus: inner border + 20% background (Figma spec)
        "focus-visible:bg-sifted-20 focus-visible:shadow-[inset_0_0_0_2px_rgb(216,28,95)]",
      ].join(" "),
    },

    // Pro Secondary
    {
      variant: BUTTON_VARIANT_SECONDARY,
      accent: BUTTON_ACCENT_PRO,
      className: [
        "text-pro",
        // Default inset border (inset shadow = no layout impact, matches Figma inside stroke)
        "shadow-[inset_0_0_0_2px_rgb(35,34,255)]",
        // Hover: 10% opacity background overlay (Figma: rgba(51,80,219,0.1))
        "hover:bg-pro-10",
        // Active: 20% opacity background overlay (Figma: rgba(51,80,219,0.2))
        "active:bg-pro-20",
        // Focus: inner border + 20% background (Figma spec)
        "focus-visible:bg-pro-20 focus-visible:shadow-[inset_0_0_0_2px_rgb(35,34,255)]",
      ].join(" "),
    },

    // Neutral Secondary
    {
      variant: BUTTON_VARIANT_SECONDARY,
      accent: BUTTON_ACCENT_NEUTRAL,
      className: [
        "text-brand-neutral",
        // Default inset border (inset shadow = no layout impact, matches Figma inside stroke)
        "shadow-[inset_0_0_0_2px_rgb(2,2,49)]",
        // Hover: 10% opacity background overlay (Figma: rgba(2,2,49,0.1))
        "hover:bg-brand-neutral-10",
        // Active: 20% opacity background overlay (Figma: rgba(2,2,49,0.2))
        "active:bg-brand-neutral-20",
        // Focus: inner border + 20% background (Figma spec)
        "focus-visible:bg-brand-neutral-20 focus-visible:shadow-[inset_0_0_0_2px_rgb(2,2,49)]",
      ].join(" "),
    },

    {
      variant: BUTTON_VARIANT_SECONDARY,
      accent: BUTTON_ACCENT_SUMMIT,
      className: [
        "bg-black text-summit-gradient-start",
        "shadow-[inset_0_0_0_2px_rgb(241,80,103)]",
        "hover:bg-summit-gradient-start/10",
        "active:bg-summit-gradient-start/20",
        "focus-visible:bg-summit-gradient-start/20 focus-visible:shadow-[inset_0_0_0_2px_rgb(241,80,103)]",
      ].join(" "),
    },

    {
      variant: BUTTON_VARIANT_SECONDARY,
      accent: BUTTON_ACCENT_SUMMIT_2,
      className: [
        "bg-black text-summit-gradient-2-start",
        "shadow-[inset_0_0_0_2px_rgb(0,208,200)]",
        "hover:bg-summit-gradient-2-start/10",
        "active:bg-summit-gradient-2-start/20",
        "focus-visible:bg-summit-gradient-2-start/20 focus-visible:shadow-[inset_0_0_0_2px_rgb(0,208,200)]",
      ].join(" "),
    },

    // ============================================================================
    // DISABLED STATES
    // ============================================================================

    // Primary Disabled
    {
      disabled: true,
      variant: BUTTON_VARIANT_PRIMARY,
      className: "bg-mono-15 text-mono-30",
    },

    // Secondary Disabled
    {
      disabled: true,
      variant: BUTTON_VARIANT_SECONDARY,
      className:
        "bg-mono-15 text-mono-30 shadow-[inset_0_0_0_2px_rgb(142,142,153)]",
    },
  ],
  defaultVariants: {
    size: BUTTON_SIZE_MD,
    variant: BUTTON_VARIANT_SECONDARY,
    accent: BUTTON_ACCENT_SIFTED,
  },
});

export type SimpleButtonProps = {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  accent?: ButtonAccent; // color accent
  variant?: ButtonVisualVariant; // primary (solid) / secondary (outlined)
  size?: "sm" | "md" | "lg";
  className?: string;
  "data-testid"?: string;
  "aria-label"?: string;
  "aria-expanded"?: React.AriaAttributes["aria-expanded"];
  "aria-controls"?: string;
};

export const SimpleButton: React.FC<SimpleButtonProps> = ({
  children,
  onClick,
  type = "button",
  disabled,
  accent,
  variant,
  size,
  className,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
  "aria-expanded": ariaExpanded,
  "aria-controls": ariaControls,
}) => {
  return (
    <button
      data-testid={dataTestId}
      type={type}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      className={buttonStyles({
        variant,
        accent,
        size,
        disabled: disabled ? true : undefined,
        className,
      })}
    >
      {children}
    </button>
  );
};

export default SimpleButton;
